package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.data.settings.SettingsManager
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.account.AccountEventBus
import com.daytimegaming.ourdailyfinances.domain.billing.SubscriptionManager
import com.daytimegaming.ourdailyfinances.domain.model.Account
import com.daytimegaming.ourdailyfinances.domain.model.RealEstateHolding
import com.daytimegaming.ourdailyfinances.domain.model.UserSubscription
import com.daytimegaming.ourdailyfinances.domain.plaid.PlaidEventBus
import com.daytimegaming.ourdailyfinances.domain.plaid.PlaidItemsEventBus
import com.daytimegaming.ourdailyfinances.domain.realestate.RealEstateEventBus
import com.daytimegaming.ourdailyfinances.domain.repository.AuthRepository
import com.daytimegaming.ourdailyfinances.domain.repository.UserRepository
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.RealEstateUseCase
import com.daytimegaming.ourdailyfinances.presentation.util.findLatestIsoTimestamp
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull

sealed class AccountsScreenState {
    data object Loading : AccountsScreenState()
    data class Error(val message: String) : AccountsScreenState()
    data class Loaded(
        val accounts: List<Account>,
        val realEstateHoldings: List<RealEstateHolding> = emptyList(),
        val isAddingAccount: Boolean = false,
        val isLinkingAccount: Boolean = false,
        val accountOrder: List<String> = emptyList(),
        val userSubscription: UserSubscription? = null,
        val premiumLimitDialogMessage: String? = null,
        val showPremiumUpgradeBottomSheet: Boolean = false,
        val premiumUpgradeReason: String? = null,
        val lastSync: String? = null,
    ) : AccountsScreenState()
}

class AccountsViewModel(
    private val accountUseCase: AccountUseCase,
    private val plaidEventBus: PlaidEventBus,
    private val plaidItemsEventBus: PlaidItemsEventBus,
    private val settingsManager: SettingsManager,
    private val authRepository: AuthRepository,
    private val realEstateUseCase: RealEstateUseCase,
    private val realEstateEventBus: RealEstateEventBus,
    private val userRepository: UserRepository,
    private val subscriptionManager: SubscriptionManager,
    private val accountEventBus: AccountEventBus = AccountEventBus(),
) : ViewModel() {

    private val _state = MutableStateFlow<AccountsScreenState>(AccountsScreenState.Loading)
    val state = _state.asStateFlow()

    private val _isRefreshing = MutableStateFlow(false)
    val isRefreshing: StateFlow<Boolean> = _isRefreshing.asStateFlow()

    private var refreshDeferred: CompletableDeferred<Unit>? = null

    private val _plaidLinkTokenEvent = MutableSharedFlow<String>(extraBufferCapacity = 1)
    val plaidLinkTokenEvent: SharedFlow<String> = _plaidLinkTokenEvent.asSharedFlow()

    private var currentUserId: String? = null
    private var loadJob: kotlinx.coroutines.Job? = null

    init {
        viewModelScope.launch {
            subscriptionManager.loadProducts()
        }
        viewModelScope.launch {
            authRepository.currentUser().collect { user ->
                currentUserId = user?.uid
                load()
            }
        }
        viewModelScope.launch {
            plaidEventBus.events.collect { result ->
                _state.update { s ->
                    if (s is AccountsScreenState.Loaded) s.copy(isLinkingAccount = true) else s
                }
                try {
                    accountUseCase.ExchangePublicToken(result.publicToken, result.institutionName)
                    load()
                } catch (e: Exception) {
                    _state.update { AccountsScreenState.Error(e.message ?: "Failed to link account") }
                }
            }
        }
        viewModelScope.launch {
            plaidItemsEventBus.events.collect { load() }
        }
        viewModelScope.launch {
            plaidItemsEventBus.orderEvents.collect { refreshOrder() }
        }
        viewModelScope.launch {
            realEstateEventBus.events.collect { load() }
        }
        viewModelScope.launch {
            accountEventBus.events.collect { load() }
        }
    }

    fun setAndroidActivity(activity: Any?) {
        subscriptionManager.setAndroidActivity(activity)
    }

    fun upgradeSubscription(tier: String) {
        viewModelScope.launch {
            val productId = if (tier.lowercase() == "premium") {
                "subscription_premium_monthly"
            } else {
                "subscription_standard_monthly"
            }
            subscriptionManager.buyProduct(productId)
        }
    }

    fun refresh() {
        if (_isRefreshing.value) return
        val deferred = CompletableDeferred<Unit>()
        refreshDeferred = deferred
        _isRefreshing.value = true
        viewModelScope.launch {
            try {
                val profileJob = launch { runCatching { userRepository.getProfile() } }
                load()
                withTimeoutOrNull(10_000) {
                    profileJob.join()
                    deferred.await()
                }
            } finally {
                _isRefreshing.value = false
                if (refreshDeferred == deferred) {
                    refreshDeferred = null
                }
            }
        }
    }

    fun load() {
        loadJob?.cancel()
        loadJob = viewModelScope.launch {
            combine(
                accountUseCase.GetAccounts(),
                accountUseCase.GetPlaidItems(),
                realEstateUseCase.GetRealEstateHoldings(),
                userRepository.currentUserProfile
            ) { accountsResponse, plaidItemsResponse, realEstateResponse, profile ->
                val isDone = accountsResponse !is Response.Loading &&
                    plaidItemsResponse !is Response.Loading &&
                    realEstateResponse !is Response.Loading

                val screenState = when {
                    accountsResponse is Response.Error ->
                        AccountsScreenState.Error(accountsResponse.message)
                    plaidItemsResponse is Response.Error ->
                        AccountsScreenState.Error(plaidItemsResponse.message)
                    realEstateResponse is Response.Error ->
                        AccountsScreenState.Error(realEstateResponse.message)
                    accountsResponse is Response.Success && plaidItemsResponse is Response.Success && realEstateResponse is Response.Success -> {
                        val items = plaidItemsResponse.data
                        val mappedAccounts = accountsResponse.data.map { account ->
                            val institutionName = items.find { it.itemId == account.itemId }?.institutionName
                            account.copy(institutionName = institutionName)
                        }

                        val userId = currentUserId
                        val savedOrder = if (userId != null) settingsManager.getAccountOrder(userId) else emptyList()
                        val orderedAccounts = if (savedOrder.isNotEmpty()) {
                            mappedAccounts.sortedWith { a1, a2 ->
                                val idx1 = savedOrder.indexOf(a1.accountId)
                                val idx2 = savedOrder.indexOf(a2.accountId)
                                when {
                                    idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                                    idx1 != -1 -> -1
                                    idx2 != -1 -> 1
                                    else -> 0
                                }
                            }
                        } else {
                            mappedAccounts
                        }
                        val orderedHoldings = if (savedOrder.isNotEmpty()) {
                            realEstateResponse.data.sortedWith { h1, h2 ->
                                val idx1 = savedOrder.indexOf(h1.realEstateHoldingId)
                                val idx2 = savedOrder.indexOf(h2.realEstateHoldingId)
                                when {
                                    idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                                    idx1 != -1 -> -1
                                    idx2 != -1 -> 1
                                    else -> 0
                                }
                            }
                        } else {
                            realEstateResponse.data
                        }
                        
                        val lastSyncTimestamp = findLatestIsoTimestamp(
                            items.flatMap { item ->
                                listOf(
                                    item.balancesLastSyncedAt,
                                    item.transactionsLastSyncedAt,
                                    item.recurringLastSyncedAt,
                                    item.investmentsLastSyncedAt
                                )
                            }
                        )
                        
                        AccountsScreenState.Loaded(
                            accounts = orderedAccounts,
                            realEstateHoldings = orderedHoldings,
                            accountOrder = savedOrder,
                            userSubscription = profile?.subscription,
                            lastSync = lastSyncTimestamp
                        )
                    }
                    else -> AccountsScreenState.Loading
                }
                screenState to isDone
            }.collect { (nextState, isDone) ->
                _state.update { s ->
                    if (s is AccountsScreenState.Loaded) {
                        if (nextState is AccountsScreenState.Loaded) {
                            nextState.copy(
                                isAddingAccount = s.isAddingAccount,
                                isLinkingAccount = s.isLinkingAccount,
                                premiumLimitDialogMessage = s.premiumLimitDialogMessage,
                                showPremiumUpgradeBottomSheet = s.showPremiumUpgradeBottomSheet,
                                premiumUpgradeReason = s.premiumUpgradeReason
                            )
                        } else s
                    } else {
                        nextState
                    }
                }
                if (isDone) {
                    refreshDeferred?.complete(Unit)
                }
            }
        }
    }

    fun refreshOrder() {
        val currentState = _state.value
        if (currentState is AccountsScreenState.Loaded) {
            val userId = currentUserId ?: return
            val savedOrder = settingsManager.getAccountOrder(userId)
            if (savedOrder.isNotEmpty()) {
                val sortedAccounts = currentState.accounts.sortedWith { a1, a2 ->
                    val idx1 = savedOrder.indexOf(a1.accountId)
                    val idx2 = savedOrder.indexOf(a2.accountId)
                    when {
                        idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                        idx1 != -1 -> -1
                        idx2 != -1 -> 1
                        else -> 0
                    }
                }
                val sortedHoldings = currentState.realEstateHoldings.sortedWith { h1, h2 ->
                    val idx1 = savedOrder.indexOf(h1.realEstateHoldingId)
                    val idx2 = savedOrder.indexOf(h2.realEstateHoldingId)
                    when {
                        idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                        idx1 != -1 -> -1
                        idx2 != -1 -> 1
                        else -> 0
                    }
                }
                _state.update { currentState.copy(accounts = sortedAccounts, realEstateHoldings = sortedHoldings, accountOrder = savedOrder) }
            }
        }
    }

    private var isRequestingToken = false

    fun requestAddAccount() {
        val currentState = _state.value
        if (currentState !is AccountsScreenState.Loaded) return
        val isAlreadyAdding = currentState.isAddingAccount
        if (isRequestingToken || isAlreadyAdding) return

        val subscription = currentState.userSubscription
        val tier = if (subscription?.active == true) subscription.tier.lowercase() else "free"
        val maxAccounts = when (tier) {
            "premium" -> 12
            "standard" -> 3
            else -> 0
        }

        if (currentState.accounts.size >= maxAccounts) {
            if (tier == "premium") {
                _state.update { s ->
                    if (s is AccountsScreenState.Loaded) {
                        s.copy(
                            premiumLimitDialogMessage = "You have reached the maximum limit of 12 financial institutions for your Premium subscription."
                        )
                    } else s
                }
            } else {
                _state.update { s ->
                    if (s is AccountsScreenState.Loaded) {
                        s.copy(
                            showPremiumUpgradeBottomSheet = true,
                            premiumUpgradeReason = "You have reached the limit of 3 financial institutions on the Standard tier."
                        )
                    } else s
                }
            }
            return
        }

        isRequestingToken = true
        _state.update { s -> if (s is AccountsScreenState.Loaded) s.copy(isAddingAccount = true) else s }

        viewModelScope.launch {
            try {
                val token = accountUseCase.CreateLinkToken()
                _state.update { s -> if (s is AccountsScreenState.Loaded) s.copy(isAddingAccount = false) else s }
                _plaidLinkTokenEvent.emit(token)
            } catch (e: Exception) {
                _state.update { AccountsScreenState.Error(e.message ?: "Failed to start account linking") }
            } finally {
                isRequestingToken = false
            }
        }
    }

    fun checkCanAddRealEstate(onAllowed: () -> Unit) {
        val currentState = _state.value as? AccountsScreenState.Loaded ?: run {
            onAllowed()
            return
        }

        val subscription = currentState.userSubscription
        val tier = if (subscription?.active == true) subscription.tier.lowercase() else "free"
        val maxRealEstate = when (tier) {
            "premium" -> 4
            else -> 0
        }

        if (currentState.realEstateHoldings.size >= maxRealEstate) {
            if (tier == "premium") {
                _state.update { s ->
                    if (s is AccountsScreenState.Loaded) {
                        s.copy(
                            premiumLimitDialogMessage = "You have reached the maximum limit of 4 real estate holdings for your Premium subscription."
                        )
                    } else s
                }
            } else {
                _state.update { s ->
                    if (s is AccountsScreenState.Loaded) {
                        s.copy(
                            showPremiumUpgradeBottomSheet = true,
                            premiumUpgradeReason = "Real estate holdings are a Premium feature."
                        )
                    } else s
                }
            }
            return
        }

        onAllowed()
    }

    fun dismissPremiumLimitDialog() {
        _state.update { s ->
            if (s is AccountsScreenState.Loaded) s.copy(premiumLimitDialogMessage = null) else s
        }
    }

    fun dismissPremiumUpgradeBottomSheet() {
        _state.update { s ->
            if (s is AccountsScreenState.Loaded) s.copy(showPremiumUpgradeBottomSheet = false, premiumUpgradeReason = null) else s
        }
    }
}
